import { useSetToast } from "@app/components/Toast";
import { useExportEvidenceFiles } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/data";
import {
    RESOURCE_NAME as LOCALES_RESOURCE_NAME,
    keys as localeKeys,
} from "@app/shared/utils/locales";
import { Button } from "@design-system/Inputs/Button";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { ResponseStatus } from "@generated/client/graphql";
import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { useExportSupplierQuestionnaire } from "../../data";
import { type SupplierQuestionnaireListItem } from "../../types";
import { useExportQuestionnaireDropdownTranslations } from "./i18n";

const ExportAnswersItem: FC<{
    locale: string;
    questionnaireId: string;
    onExport: (questionnaireId: string, locale: string) => void;
}> = ({ locale, questionnaireId, onExport }) => {
    const { t, keys } = useExportQuestionnaireDropdownTranslations();
    const { t: tLocale } = useTranslation(LOCALES_RESOURCE_NAME);
    const localeName = tLocale(
        localeKeys[locale as keyof typeof localeKeys] ?? locale,
    );

    const handleClick = () => {
        onExport(questionnaireId, locale);
    };

    return (
        <Dropdown.Item onClick={handleClick} justifyContent="between">
            <Dropdown.ItemText>
                {t(keys.export_answers_in_locale, { locale: localeName })}
            </Dropdown.ItemText>
            <Dropdown.ItemIcon name="arrowDownload" />
        </Dropdown.Item>
    );
};

export const ExportQuestionnaireDropdown: FC<{
    questionnaire: SupplierQuestionnaireListItem;
}> = ({ questionnaire }) => {
    const {
        exportSupplierQuestionnaire,
        isPending: isExportSupplierQuestionnairePending,
    } = useExportSupplierQuestionnaire();

    const { exportEvidenceFiles, isPending: isExportEvidenceFilesPending } =
        useExportEvidenceFiles();

    const { setToastError } = useSetToast();

    const { t, keys } = useExportQuestionnaireDropdownTranslations();

    const isExportLoading =
        isExportSupplierQuestionnairePending || isExportEvidenceFilesPending;

    const availableLocales = questionnaire.locale?.availableLocales ?? [];

    const handleExportAnswers = (questionnaireId: string, locale: string) => {
        exportSupplierQuestionnaire(
            {
                questionnaireId,
                locale,
            },
            {
                onSettled: (data, error) => {
                    if (
                        data?.exportSupplierQuestionnaire?.status !==
                            ResponseStatus.Success ||
                        !data?.exportSupplierQuestionnaire?.data?.signedUrl ||
                        error
                    ) {
                        return setToastError(t(keys.download_error));
                    }
                },
            },
        );
    };

    const handleExportFilesClick = () => {
        if (!questionnaire) return;
        exportEvidenceFiles(questionnaire.id, {
            onSettled: (data, error) => {
                if (error || !data?.exportEvidenceFiles.data?.signedUrl) {
                    return setToastError(t(keys.download_error));
                }
            },
        });
    };

    return (
        <Dropdown>
            <Dropdown.Trigger asChild>
                <Button variant="outlined" size="sm" loading={isExportLoading}>
                    <Button.Icon name="arrowDownload" />
                </Button>
            </Dropdown.Trigger>
            <Dropdown.Content hasPortal>
                {availableLocales.map((locale) => (
                    <ExportAnswersItem
                        key={locale}
                        locale={locale}
                        questionnaireId={questionnaire.id}
                        onExport={handleExportAnswers}
                    />
                ))}
                <Dropdown.Item
                    onClick={handleExportFilesClick}
                    justifyContent="between"
                >
                    <Dropdown.ItemText>
                        {t(keys.export_files)}
                    </Dropdown.ItemText>
                    <Dropdown.ItemIcon name="arrowDownload" />
                </Dropdown.Item>
            </Dropdown.Content>
        </Dropdown>
    );
};
